import { useEffect, useState } from "react";
import { Button } from "primereact/button";

import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";

import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";
import FieldNote from "./common/FieldNote";

export default function WebsiteSettings() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/settings",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Site Settings");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/settings",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Site Settings !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Site Settings Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Site Settings !", life: 2000 });
                }
            },
        });
    };

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Site Settings" />
            <TabHeader
                className="px-3 pt-3"
                title="Site Settings"
                highlights={["The name, tagline, and logo appear on every page", "Phone, WhatsApp, and email are also the Institute Contact cards"]}
            />
            <Divider />
            {loading ? <Loading /> : null}
            {error ? <NoContent error={error} /> : null}
            {!loading && !error && record ? (
                <div className="flex-1 overflow-y-scroll p-3 flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="site_name" className="w-full" value={record?.site_name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, site_name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="site_name">Site name</label>
                        </FloatLabel>
                        <FieldNote text="Required. Shown in the header and footer, and used as the browser title when a page has no search title of its own." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="site_tagline" className="w-full" value={record?.site_tagline ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, site_tagline: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="site_tagline">Tagline</label>
                        </FloatLabel>
                        <FieldNote text="The small line under the name in the header, when the top bar does not set its own line." />
                    </div>
                    <WebsiteMediaInput label="Site logo" field="site_logo" hint="Required size: 256 ? 256 px (square). A larger logo is scaled to fit the header, footer, and browser tab." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="site_phone" className="w-full" value={record?.site_phone ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, site_phone: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="site_phone">Phone</label>
                        </FloatLabel>
                        <FieldNote text="The Phone card on Contact Us, and the phone in the header and footer. Leave blank to hide it." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="site_whatsapp" className="w-full" value={record?.site_whatsapp ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, site_whatsapp: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="site_whatsapp">WhatsApp link</label>
                        </FloatLabel>
                        <FieldNote text="The WhatsApp card on Contact Us. Use the full chat link, starting with https://. Leave blank to hide it." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="site_email" className="w-full" value={record?.site_email ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, site_email: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="site_email">Email</label>
                        </FloatLabel>
                        <FieldNote text="The Email card on Contact Us, and the email in the footer. Leave blank to hide it." />
                    </div>
                    <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
                </div>
            ) : null}
        </div>
    );
}
